import { supabase } from "@/integrations/supabase/client";

/** The shape every people list, follow button and profile header shares. */
export interface PersonSummary {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  account_type: string | null;
  verified: boolean | null;
}

const PERSON_COLS = "id, username, full_name, avatar_url, bio, location, account_type, verified";

/** Follower / following / communities counts for a profile header. */
export async function fetchRelationshipCounts(userId: string) {
  const [followers, following, communities] = await Promise.all([
    supabase
      .from("follows")
      .select("follower_id", { count: "exact", head: true })
      .eq("following_id", userId),
    supabase
      .from("follows")
      .select("following_id", { count: "exact", head: true })
      .eq("follower_id", userId),
    supabase
      .from("community_members")
      .select("community_id", { count: "exact", head: true })
      .eq("user_id", userId),
  ]);
  return {
    followers: followers.count ?? 0,
    following: following.count ?? 0,
    communities: communities.count ?? 0,
  };
}

export async function isFollowing(viewerId: string, targetId: string) {
  if (viewerId === targetId) return false;
  const { data } = await supabase
    .from("follows")
    .select("follower_id")
    .eq("follower_id", viewerId)
    .eq("following_id", targetId)
    .maybeSingle();
  return !!data;
}

export async function follow(viewerId: string, targetId: string) {
  const { error } = await supabase
    .from("follows")
    .insert({ follower_id: viewerId, following_id: targetId });
  if (error && !/duplicate key/i.test(error.message)) throw error;
}

export async function unfollow(viewerId: string, targetId: string) {
  const { error } = await supabase
    .from("follows")
    .delete()
    .eq("follower_id", viewerId)
    .eq("following_id", targetId);
  if (error) throw error;
}

/** People who follow `userId`. */
export async function fetchFollowers(userId: string, limit = 200): Promise<PersonSummary[]> {
  const { data } = await supabase
    .from("follows")
    .select(`follower_id, profiles!follows_follower_id_fkey ( ${PERSON_COLS} )`)
    .eq("following_id", userId)
    .limit(limit);
  return ((data ?? []) as any[]).map((r) => r.profiles).filter(Boolean) as PersonSummary[];
}

/** People `userId` follows. */
export async function fetchFollowing(userId: string, limit = 200): Promise<PersonSummary[]> {
  const { data } = await supabase
    .from("follows")
    .select(`following_id, profiles!follows_following_id_fkey ( ${PERSON_COLS} )`)
    .eq("follower_id", userId)
    .limit(limit);
  return ((data ?? []) as any[]).map((r) => r.profiles).filter(Boolean) as PersonSummary[];
}

/** Ids the viewer already follows — used to set button state across lists. */
export async function fetchFollowingIds(viewerId: string): Promise<Set<string>> {
  const { data } = await supabase
    .from("follows")
    .select("following_id")
    .eq("follower_id", viewerId);
  return new Set(((data ?? []) as { following_id: string }[]).map((r) => r.following_id));
}

/** Everything the viewer's own feed should draw from. */
export async function fetchFeedSources(viewerId: string) {
  const [f, c] = await Promise.all([
    supabase.from("follows").select("following_id").eq("follower_id", viewerId),
    supabase.from("community_members").select("community_id").eq("user_id", viewerId),
  ]);
  return {
    authorIds: ((f.data ?? []) as { following_id: string }[]).map((r) => r.following_id),
    communityIds: ((c.data ?? []) as { community_id: string }[]).map((r) => r.community_id),
  };
}

/** People discovery: real profiles, excluding private ones and the viewer. */
export async function discoverPeople(
  opts: { query?: string; viewerId?: string; official?: boolean; limit?: number } = {},
) {
  let q = supabase.from("profiles").select(PERSON_COLS).neq("privacy_profile", "private");
  if (opts.official) q = q.neq("account_type", "citizen").not("account_type", "is", null);
  if (opts.query?.trim()) {
    const like = `%${opts.query.trim()}%`;
    q = q.or(`username.ilike.${like},full_name.ilike.${like},location.ilike.${like}`);
  }
  if (opts.viewerId) q = q.neq("id", opts.viewerId);
  const { data } = await q.order("impact_score", { ascending: false }).limit(opts.limit ?? 30);
  return (data ?? []) as unknown as PersonSummary[];
}
